import React, { useState, useEffect } from "react";
import { VITE_SUPABASE_URL, VITE_SUPABASE_ANON_KEY } from "@/utils/const";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
	Calendar,
	DollarSign,
	MessageSquare,
	Users,
	ArrowRight,
	CheckCircle,
	XCircle,
	AlertCircle,
	CalendarDays,
	Clock3,
	Baby,
	Star,
	Package,
	Activity,
	Check,
	X,
} from "lucide-react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

interface Appointment {
	id: string;
	appointment_date: string;
	duration_minutes: number;
	status: "pending" | "accepted" | "declined" | "cancelled" | "completed";
	notes?: string;
	meeting_link?: string;
	meeting_id?: string;
	parent: {
		id: string;
		full_name: string;
		avatar_url?: string;
		email: string;
	};
	parent_profile: {
		due_date: string;
		birth_location?: string;
	};
}

interface Conversation {
	id: string;
	created_at: string;
	parent_user: {
		full_name: string;
		avatar_url?: string;
	};
	messages: Array<{
		content: string;
		created_at: string;
	}>;
}

interface EarningsSummary {
	total_earnings: number;
	this_month_earnings: number;
	pending_payments: number;
	completed_transactions: number;
	pending_transactions: number;
	average_commission: number;
	total_transactions: number;
}

interface Transaction {
	id: string;
	total_amount: number;
	doula_amount: number;
	platform_commission: number;
	commission_percentage: number;
	status: string;
	created_at: string;
	parent_name: string;
	package_name: string;
}

interface DoulaStats {
	total_clients: number;
	active_conversations: number;
	recent_conversations: number;
	response_rate: number;
	total_earnings: number;
	packages_count: number;
	total_appointments: number;
	accepted_appointments: number;
	completed_transactions: number;
}

interface Conversations {
	id: string | number | null;
	created_at: string;
	parent_user: string | null;
	messages: Array<object> | null;
}

interface DashboardData {
	appointments: Appointment[];
	conversations: Conversation[];
	earnings: EarningsSummary;
	transactions: Transaction[];
	stats: DoulaStats;
}

const DoulaDashboard: React.FC = () => {
	const { user, profile } = useAuth();
	const navigate = useNavigate();
	const [loading, setLoading] = useState(true);
	const [dashboardData, setDashboardData] = useState<DashboardData>({
		appointments: [],
		conversations: [],
		earnings: {
			total_earnings: 0,
			this_month_earnings: 0,
			pending_payments: 0,
			completed_transactions: 0,
			pending_transactions: 0,
			average_commission: 0,
			total_transactions: 0,
		},
		transactions: [],
		stats: {
			total_clients: 0,
			active_conversations: 0,
			recent_conversations: 0,
			response_rate: 0,
			total_earnings: 0,
			packages_count: 0,
			total_appointments: 0,
			accepted_appointments: 0,
			completed_transactions: 0,
		},
	});

	useEffect(() => {
		if (user) {
			fetchDashboardData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [user?.id]);

	const fetchDashboardData = async () => {
		if (!user) return;

		try {
			setLoading(true);

			// Get the public user ID using edge function (no direct DB calls)
			const userRoleResponse = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/get-user-role`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${VITE_SUPABASE_ANON_KEY}`,
					},
					body: JSON.stringify({
						authUserId: user.id,
					}),
				},
			);

			const userRoleResult = await userRoleResponse.json();
			if (!userRoleResponse.ok || !userRoleResult.profile) {
				console.error("Error fetching user profile:", userRoleResult.error);
				return;
			}

			const doulaUserId = userRoleResult.profile.id;

			// Fetch appointments using edge function
			let appointments: Appointment[] = [];
			try {
				const appointmentsResponse = await fetch(
					`${VITE_SUPABASE_URL}/functions/v1/get-doula-appointments`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${VITE_SUPABASE_ANON_KEY}`,
						},
						body: JSON.stringify({
							doulaUserId: doulaUserId,
							includePast: false, // Only get future appointments for dashboard
						}),
					},
				);

				const appointmentsResult = await appointmentsResponse.json();
				if (!appointmentsResponse.ok) {
					console.error(
						"Error loading appointments:",
						appointmentsResult.error,
					);
				} else {
					appointments = appointmentsResult.data || [];
				}
			} catch (error) {
				console.error("Error loading appointments:", error);
			}

			// Fetch conversations using edge function - fixed parameters
			let conversations: Conversation[] = [];
			try {
				const conversationsResponse = await fetch(
					`${VITE_SUPABASE_URL}/functions/v1/manage-conversations`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${VITE_SUPABASE_ANON_KEY}`,
						},
						body: JSON.stringify({
							action: "get_conversations_for_doula",
							doulaUserId: doulaUserId,
						}),
					},
				);

				const conversationsResult = await conversationsResponse.json();
				if (!conversationsResponse.ok) {
					console.error(
						"Error loading conversations:",
						conversationsResult.error,
					);
				} else {
					// Transform the response to match our interface
					conversations = (conversationsResult.data || []).map((conv: Conversations) => ({
						id: conv.id,
						created_at: conv.created_at,
						parent_user: conv.parent_user,
						messages: conv.messages || [],
					}));
				}
			} catch (error) {
				console.error("Error loading conversations:", error);
			}

			// Fetch earnings using edge function
			let earnings: EarningsSummary = {
				total_earnings: 0,
				this_month_earnings: 0,
				pending_payments: 0,
				completed_transactions: 0,
				pending_transactions: 0,
				average_commission: 0,
				total_transactions: 0,
			};
			let transactions: Transaction[] = [];

			try {
				const earningsResponse = await fetch(
					`${VITE_SUPABASE_URL}/functions/v1/get-doula-earnings`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${VITE_SUPABASE_ANON_KEY}`,
						},
						body: JSON.stringify({
							doulaUserId: doulaUserId,
						}),
					},
				);

				const earningsResult = await earningsResponse.json();
				if (!earningsResponse.ok) {
					console.error("Error loading earnings:", earningsResult.error);
				} else {
					earnings = earningsResult.data.summary || earnings;
					transactions = earningsResult.data.transactions || [];
				}
			} catch (error) {
				console.error("Error loading earnings:", error);
			}

			// Fetch stats using edge function
			let stats: DoulaStats = {
				total_clients: 0,
				active_conversations: 0,
				recent_conversations: 0,
				response_rate: 0,
				total_earnings: 0,
				packages_count: 0,
				total_appointments: 0,
				accepted_appointments: 0,
				completed_transactions: 0,
			};

			try {
				const statsResponse = await fetch(
					`${VITE_SUPABASE_URL}/functions/v1/get-doula-stats`,
					{
						method: "POST",
						headers: {
							"Content-Type": "application/json",
							Authorization: `Bearer ${VITE_SUPABASE_ANON_KEY}`,
						},
						body: JSON.stringify({
							doulaUserId: doulaUserId,
						}),
					},
				);

				const statsResult = await statsResponse.json();
				if (!statsResponse.ok) {
					console.error("Error loading stats:", statsResult.error);
				} else {
					stats = statsResult.data || stats;
				}
			} catch (error) {
				console.error("Error loading stats:", error);
			}

			setDashboardData({
				appointments,
				conversations,
				earnings,
				transactions,
				stats,
			});
		} catch (error) {
			console.error("Error fetching dashboard data:", error);
		} finally {
			setLoading(false);
		}
	};

	const formatAppointmentDate = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleDateString("en-US", {
			weekday: "long",
			year: "numeric",
			month: "long",
			day: "numeric",
		});
	};

	const formatAppointmentTime = (dateString: string) => {
		const date = new Date(dateString);
		return date.toLocaleTimeString("en-US", {
			hour: "numeric",
			minute: "2-digit",
			hour12: true,
		});
	};

	const getTimeUntilAppointment = (dateString: string) => {
		const appointmentDate = new Date(dateString);
		const now = new Date();
		const diff = appointmentDate.getTime() - now.getTime();

		if (diff < 0) return null; // Past appointment

		const days = Math.floor(diff / (1000 * 60 * 60 * 24));
		const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
		const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

		if (days > 0) {
			return `${days} day${days > 1 ? "s" : ""} ${hours} hour${hours > 1 ? "s" : ""}`;
		} else if (hours > 0) {
			return `${hours} hour${hours > 1 ? "s" : ""} ${minutes} minute${minutes > 1 ? "s" : ""}`;
		} else {
			return `${minutes} minute${minutes > 1 ? "s" : ""}`;
		}
	};

	const getStatusColor = (status: string) => {
		switch (status) {
			case "completed":
				return "bg-green-100 text-green-800 border-green-200";
			case "accepted":
				return "bg-blue-100 text-blue-800 border-blue-200";
			case "pending":
				return "bg-yellow-100 text-yellow-800 border-yellow-200";
			case "declined":
				return "bg-red-100 text-red-800 border-red-200";
			case "cancelled":
				return "bg-gray-100 text-gray-800 border-gray-200";
			default:
				return "bg-gray-100 text-gray-800 border-gray-200";
		}
	};

	const getStatusIcon = (status: string) => {
		switch (status) {
			case "completed":
				return <CheckCircle className="h-4 w-4" />;
			case "accepted":
				return <CheckCircle className="h-4 w-4" />;
			case "pending":
				return <Clock3 className="h-4 w-4" />;
			case "declined":
				return <XCircle className="h-4 w-4" />;
			case "cancelled":
				return <XCircle className="h-4 w-4" />;
			default:
				return <AlertCircle className="h-4 w-4" />;
		}
	};

	const canJoinMeeting = (appointmentDate: string) => {
		const appointmentTime = new Date(appointmentDate);
		const now = new Date();
		const tenMinutesBefore = new Date(
			appointmentTime.getTime() - 10 * 60 * 1000,
		);
		return now >= tenMinutesBefore;
	};

	const handleJoinMeeting = (appointment: Appointment) => {
		if (appointment.meeting_link) {
			window.open(appointment.meeting_link, "_blank");
		}
	};

	const handleMessageParent = (appointment: Appointment) => {
		navigate("/doula-messages", {
			state: {
				parentUserId: appointment.parent.id,
				appointmentId: appointment.id,
			},
		});
	};

	const handleAcceptAppointment = async (appointmentId: string) => {
		try {
			setLoading(true);

			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/update-appointment-status`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						appointmentId: appointmentId,
						status: "accepted",
					}),
				},
			);

			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(data.error || "Failed to accept appointment");
			}

			// Refresh dashboard data to show updated status
			await fetchDashboardData();
		} catch (error: unknown | null | undefined) {
			console.error("Error accepting appointment:", error);
			// You could add a toast notification here
		} finally {
			setLoading(false);
		}
	};

	const handleDeclineAppointment = async (appointmentId: string) => {
		try {
			setLoading(true);

			const response = await fetch(
				`${VITE_SUPABASE_URL}/functions/v1/update-appointment-status`,
				{
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
					},
					body: JSON.stringify({
						appointmentId: appointmentId,
						status: "declined",
					}),
				},
			);

			const data = await response.json();

			if (!response.ok || !data.success) {
				throw new Error(data.error || "Failed to decline appointment");
			}

			// Refresh dashboard data to show updated status
			await fetchDashboardData();
		} catch (error: unknown | null | undefined) {
			console.error("Error declining appointment:", error);
			// You could add a toast notification here
		} finally {
			setLoading(false);
		}
	};

	const formatDate = (dateString: string) => {
		return new Date(dateString).toLocaleDateString("en-US", {
			month: "short",
			day: "numeric",
			year: "numeric",
		});
	};

	if (loading) {
		return (
			<div className="min-h-screen bg-gray-50 flex items-center justify-center">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
					<p className="text-gray-600 font-karla">Loading your dashboard...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
				{/* Top Section: Welcome and Quick Stats */}
				<motion.div
					initial={{ opacity: 0, y: 20 }}
					animate={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.6 }}
					className="mb-8"
				>
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
						{/* Welcome Card */}
						<motion.div
							initial={{ opacity: 0, x: -20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, delay: 0.1 }}
						>
							<Card className="glass-card border-0 shadow-lg h-full">
								<CardContent className="p-6">
									<div className="text-center">
										<div className="flex items-center justify-center mb-3">
											<Users className="h-8 w-8 text-newTheme-orange mr-2" />
											<h3 className="text-xl lg:text-[24px] text-black font-karla">
												Welcome Back!
											</h3>
										</div>
										<p className="text-2xl font-bold text-primary-900 font-karla mb-2 username">
											{profile?.full_name || user?.email || "Doula"}
										</p>
										<p className="text-sm text-primary-700 font-karla">
											You have {dashboardData.appointments.length} upcoming
											appointment
											{dashboardData.appointments.length !== 1 ? "s" : ""}
										</p>
									</div>
								</CardContent>
							</Card>
						</motion.div>

						{/* Earnings Overview Card */}
						<motion.div
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, delay: 0.2 }}
						>
							<Card className="glass-card border-0 shadow-lg bg-gradient-to-br from-primary-50 to-primary-100 border-primary-200 h-full">
								<CardContent className="p-6">
									<div className="text-center">
										<div className="flex items-center justify-center mb-3">
											<DollarSign className="h-8 w-8 text-primary-600 mr-2" />
											<h3 className="text-xl font-bold text-primary-800 font-karla">
												This Month's Earnings
											</h3>
										</div>
										<p className="text-2xl font-bold text-primary-900 font-karla mb-2">
											$
											{dashboardData.earnings.this_month_earnings.toLocaleString()}
										</p>
										<p className="text-sm text-primary-700 font-karla">
											Total: $
											{dashboardData.earnings.total_earnings.toLocaleString()}
										</p>
									</div>
								</CardContent>
							</Card>
						</motion.div>
					</div>
				</motion.div>

				<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
					{/* Main Content */}
					<div className="lg:col-span-2 space-y-6">
						{/* Quick Actions */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.3 }}
						>
							<Card className="glass-card border-0 shadow-xl">
								<CardHeader>
									<CardTitle className="text-xl font-karla">
										Quick Actions
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
										<Button
											onClick={() => navigate("/doula-appointments")}
											className="h-16 flex-col space-y-2 bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow transition-all duration-200"
										>
											<Calendar className="h-6 w-6 text-black" />
											<span className="font-karla text-black">
												View Appointments
											</span>
										</Button>
										<Button
											onClick={() => navigate("/doula-messages")}
											variant="outline"
											className="h-16 flex-col space-y-2 bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow transition-all duration-200 border-0"
										>
											<MessageSquare className="h-6 w-6 text-black" />
											<span className="font-karla text-black">
												Messages
											</span>
										</Button>
										<Button
											onClick={() => navigate("/doula-earnings")}
											variant="outline"
											className="h-16 flex-col space-y-2 bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow transition-all duration-200 border-0"
										>
											<DollarSign className="h-6 w-6" />
											<span className="font-karla">Earnings</span>
										</Button>
										<Button
											onClick={() => navigate("/doula-profile-management")}
											variant="outline"
											className="h-16 flex-col space-y-2 bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow transition-all duration-200 border-0"
										>
											<Users className="h-6 w-6" />
											<span className="font-karla">Profile</span>
										</Button>
									</div>
								</CardContent>
							</Card>
						</motion.div>

						{/* Upcoming Appointments */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.4 }}
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-xl font-karla">
										Upcoming Appointments
									</CardTitle>
								</CardHeader>
								<CardContent>
									{dashboardData.appointments.length > 0 ? (
										<div className="space-y-4">
											{dashboardData.appointments
												.filter((appointment) => {
													// Since edge function already filters for future appointments,
													// only filter by status here
													return (
														appointment.status !== "cancelled" &&
														appointment.status !== "declined"
													);
												})
												.slice(0, 3)
												.map((appointment, index) => (
													<motion.div
														key={appointment.id}
														initial={{ opacity: 0, y: 20 }}
														animate={{ opacity: 1, y: 0 }}
														transition={{
															duration: 0.6,
															delay: 0.1 + index * 0.1,
														}}
													>
														<Card className="glass-card border-0 shadow-lg hover:shadow-xl transition-all duration-300">
															<CardHeader className="pb-4">
																<div className="flex items-start justify-between">
																	<div className="flex items-center space-x-3">
																		<div className="relative">
																			<img
																				src={
																					appointment.parent.avatar_url ||
																					"/placeholder.svg"
																				}
																				alt={appointment.parent.full_name}
																				className="w-12 h-12 rounded-lg object-cover border-2 border-primary-200"
																			/>
																			<div className="absolute -bottom-1 -right-1 w-4 h-4 bg-primary-500 rounded-full border-2 border-white"></div>
																		</div>
																		<div>
																			<h3 className="font-semibold text-black font-karla">
																				{appointment.parent.full_name}
																			</h3>
																			<p className="text-sm text-black font-karla">
																				{appointment.parent_profile
																					.birth_location ||
																					"Location not specified"}
																			</p>
																		</div>
																	</div>
																	<Badge
																		variant="outline"
																		className={`text-xs ${getStatusColor(appointment.status)}`}
																	>
																		<span className="flex items-center space-x-1">
																			{getStatusIcon(appointment.status)}
																			<span className="capitalize">
																				{appointment.status}
																			</span>
																		</span>
																	</Badge>
																</div>
															</CardHeader>

															<CardContent className="space-y-4">
																{/* Appointment Details */}
																<div className="space-y-3">
																	<div className="flex items-center space-x-3">
																		<CalendarDays className="h-4 w-4 text-primary-600" />
																		<div>
																			<p className="text-sm font-medium text-gray-900 font-karla">
																				{formatAppointmentDate(
																					appointment.appointment_date,
																				)}
																			</p>
																			<p className="text-xs text-gray-600 font-karla">
																				{formatAppointmentTime(
																					appointment.appointment_date,
																				)}{" "}
																				• {appointment.duration_minutes} minutes
																			</p>
																		</div>
																	</div>

																	{appointment.parent_profile.due_date && (
																		<div className="flex items-center space-x-3">
																			<Baby className="h-4 w-4 text-primary-600" />
																			<p className="text-sm text-gray-600 font-karla">
																				Due:{" "}
																				{new Date(
																					appointment.parent_profile.due_date,
																				).toLocaleDateString()}
																			</p>
																		</div>
																	)}

																	{appointment.notes && (
																		<div className="bg-gray-50 p-3 rounded-lg">
																			<p className="text-sm text-gray-700 font-karla">
																				<strong>Notes:</strong>{" "}
																				{appointment.notes}
																			</p>
																		</div>
																	)}
																</div>

																{/* Countdown for upcoming appointments */}
																{appointment.status === "accepted" &&
																	getTimeUntilAppointment(
																		appointment.appointment_date,
																	) && (
																		<div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
																			<div className="flex items-center space-x-2">
																				<Clock3 className="h-4 w-4 text-blue-600" />
																				<p className="text-sm font-medium text-blue-800 font-karla">
																					Meeting starts in{" "}
																					{getTimeUntilAppointment(
																						appointment.appointment_date,
																					)}
																				</p>
																			</div>
																		</div>
																	)}

																{/* Action Buttons */}
																<div className="flex flex-col space-y-2 pt-2">
																	{/* Accept/Decline buttons for pending appointments */}
																	{appointment.status === "pending" && (
																		<div className="grid grid-cols-2 gap-2 mb-2">
																			<Button
																				onClick={() =>
																					handleAcceptAppointment(
																						appointment.id,
																					)
																				}
																				disabled={loading}
																				className="bg-green-500 hover:bg-green-600 text-white text-sm"
																			>
																				<Check className="h-4 w-4 mr-1" />
																				Accept
																			</Button>
																			<Button
																				onClick={() =>
																					handleDeclineAppointment(
																						appointment.id,
																					)
																				}
																				disabled={loading}
																				variant="outline"
																				className="border-red-300 text-red-600 hover:bg-red-50 text-sm"
																			>
																				<X className="h-4 w-4 mr-1" />
																				Decline
																			</Button>
																		</div>
																	)}

																	<Button
																		onClick={() =>
																			handleMessageParent(appointment)
																		}
																		variant="outline"
																		className="w-full text-sm"
																	>
																		<MessageSquare className="h-4 w-4 mr-2" />
																		Message
																	</Button>

																	{appointment.status === "accepted" &&
																		appointment.meeting_link && (
																			<Button
																				onClick={() =>
																					handleJoinMeeting(appointment)
																				}
																				disabled={
																					!canJoinMeeting(
																						appointment.appointment_date,
																					)
																				}
																				className={`w-full text-sm ${canJoinMeeting(
																					appointment.appointment_date,
																				)
																					? "bg-primary-500 hover:bg-primary-600 text-white"
																					: "bg-primary-200 text-primary-600 cursor-not-allowed"
																					}`}
																			>
																				<Calendar className="h-4 w-4 mr-2" />
																				{canJoinMeeting(
																					appointment.appointment_date,
																				)
																					? "Join Meeting"
																					: "Join Meeting (10 min before)"}
																			</Button>
																		)}
																</div>
															</CardContent>
														</Card>
													</motion.div>
												))}
										</div>
									) : (
										<div className="text-center py-8">
											<Calendar className="h-12 w-12 text-newTheme-orange mx-auto mb-4" />
											<p className="text-black font-karla mb-2">
												No upcoming appointments
											</p>
											<p className="text-sm text-black font-karla mb-4">
												When parents book appointments with you, they'll appear here.
											</p>
											<Button
												onClick={() => navigate("/doula-appointments")}
												className="bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow"
											>
												View All Appointments
											</Button>
										</div>
									)}
								</CardContent>
							</Card>
						</motion.div>

						{/* Recent Conversations */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.5 }}
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-xl font-karla">
										Recent Conversations
									</CardTitle>
								</CardHeader>
								<CardContent>
									{dashboardData.conversations.length > 0 ? (
										<div className="space-y-4">
											{dashboardData.conversations
												.slice(0, 3)
												.map((conversation, index) => (
													<motion.div
														key={conversation.id}
														initial={{ opacity: 0, x: -20 }}
														animate={{ opacity: 1, x: 0 }}
														transition={{
															duration: 0.6,
															delay: 0.6 + index * 0.1,
														}}
														className="flex items-center space-x-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer"
														onClick={() => navigate("/doula-messages")}
													>
														<div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
															<MessageSquare className="h-5 w-5 text-primary-600" />
														</div>
														<div className="flex-1">
															<h4 className="font-semibold text-gray-900 font-karla">
																{conversation.parent_user?.full_name ||
																	"Parent"}
															</h4>
															<p className="text-sm text-gray-600 font-karla">
																{conversation.messages &&
																	conversation.messages.length > 0
																	? conversation.messages[
																		conversation.messages.length - 1
																	].content.substring(0, 50) + "..."
																	: "No messages yet"}
															</p>
															<p className="text-xs text-gray-500 font-karla">
																{formatDate(conversation.created_at)}
															</p>
														</div>
														<ArrowRight className="h-4 w-4 text-gray-400" />
													</motion.div>
												))}
										</div>
									) : (
										<div className="text-center py-8">
											<MessageSquare className="h-12 w-12 text-newTheme-spruce mx-auto mb-4" />
											<p className="text-black font-karla">
												No conversations yet
											</p>
											<Button
												onClick={() => navigate("/doula-appointments")}
												className="mt-4 bg-newTheme-lime hover:bg-newTheme-orange text-black shadow-btn-shadow"
											>
												View Appointments to Start Chatting
											</Button>
										</div>
									)}
								</CardContent>
							</Card>
						</motion.div>

						{/* Recent Transactions */}
						<motion.div
							initial={{ opacity: 0, y: 20 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.6, delay: 0.6 }}
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-xl font-karla">
										Recent Transactions
									</CardTitle>
								</CardHeader>
								<CardContent>
									{dashboardData.transactions.length > 0 ? (
										<div className="space-y-4">
											{dashboardData.transactions
												.slice(0, 5)
												.map((transaction, index) => (
													<motion.div
														key={transaction.id}
														initial={{ opacity: 0, x: -20 }}
														animate={{ opacity: 1, x: 0 }}
														transition={{
															duration: 0.6,
															delay: 0.7 + index * 0.1,
														}}
														className="flex items-center space-x-4 p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors"
													>
														<div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
															<DollarSign className="h-5 w-5 text-green-600" />
														</div>
														<div className="flex-1">
															<h4 className="font-semibold text-gray-900 font-karla">
																{transaction.package_name}
															</h4>
															<p className="text-sm text-gray-600 font-karla">
																{transaction.parent_name}
															</p>
															<p className="text-xs text-gray-500 font-karla">
																{formatDate(transaction.created_at)}
															</p>
														</div>
														<div className="text-right">
															<p className="font-semibold text-gray-900">
																${transaction.doula_amount.toFixed(2)}
															</p>
															<Badge
																variant={
																	transaction.status === "completed"
																		? "default"
																		: "secondary"
																}
																className="text-xs"
															>
																{transaction.status}
															</Badge>
														</div>
													</motion.div>
												))}
										</div>
									) : (
										<div className="text-center py-8">
											<DollarSign className="h-12 w-12 text-newTheme-lime mx-auto mb-4" />
											<p className="text-black font-karla">
												No transactions yet
											</p>
										</div>
									)}
								</CardContent>
							</Card>
						</motion.div>
					</div>

					{/* Sidebar */}
					<div className="space-y-6">
						{/* Performance Overview */}
						<motion.div
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, delay: 0.3 }}
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-lg font-karla">
										Performance Overview
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="space-y-4">
										<div className="flex items-center justify-between">
											<div className="flex items-center space-x-3">
												<div className="w-10 h-10 bg-newTheme-lightBlue rounded-lg flex items-center justify-center">
													<Users className="h-5 w-5 text-newTheme-orange" />
												</div>
												<div>
													<h5 className="text-sm font-karla text-black">
														Total Clients
													</h5>
													<p className="text-lg text-black font-karla">
														{dashboardData.stats.total_clients}
													</p>
												</div>
											</div>
										</div>

										<div className="flex items-center justify-between">
											<div className="flex items-center space-x-3">
												<div className="w-10 h-10 bg-newTheme-lime rounded-lg flex items-center justify-center">
													<MessageSquare className="h-5 w-5 text-newTheme-spruce" />
												</div>
												<div>
													<h5 className="text-sm text-black font-karla">
														Active Conversations
													</h5>
													<p className="text-lg font-semibold text-black">
														{dashboardData.stats.active_conversations}
													</p>
												</div>
											</div>
										</div>

										<div className="flex items-center justify-between">
											<div className="flex items-center space-x-3">
												<div className="w-10 h-10 bg-newTheme-orange rounded-lg flex items-center justify-center">
													<Star className="h-5 w-5 text-newTheme-cotton" />
												</div>
												<div>
													<h5 className="text-sm text-black font-karla">
														Response Rate
													</h5>
													<p className="text-lg font-semibold text-black">
														{dashboardData.stats.response_rate}%
													</p>
												</div>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</motion.div>

						{/* Earnings Summary */}
						<motion.div
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, delay: 0.4 }}
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-lg font-karla">
										Earnings Summary
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="space-y-4">
										<div className="text-center">
											<div className="text-3xl font-bold text-newTheme-lime mb-2">
												$
												{dashboardData.earnings.total_earnings.toLocaleString()}
											</div>
											<p className="text-sm text-black font-karla">
												Total Earnings
											</p>
										</div>

										<div className="grid grid-cols-2 gap-4">
											<div className="text-center">
												<div className="text-xl font-bold text-newTheme-spruce">
													<span className="currency">&#36;</span>
													{dashboardData.earnings.this_month_earnings.toLocaleString()}
												</div>
												<p className="text-xs text-black font-karla">
													This Month
												</p>
											</div>

											<div className="text-center">
												<div className="text-xl font-bold text-newTheme-orange mb-1">
													<span className="currency">&#36;</span>
													{dashboardData.earnings.pending_payments.toLocaleString()}
												</div>
												<p className="text-xs text-black font-karla">
													Pending
												</p>
											</div>
										</div>

										<div className="pt-4 border-t border-gray-200">
											<div className="flex items-center justify-between text-sm">
												<span className="text-black font-karla">
													Completed Transactions
												</span>
												<span className="font-semibold">
													{dashboardData.earnings.completed_transactions}
												</span>
											</div>
											<div className="flex items-center justify-between text-sm">
												<span className="text-black font-karla">
													Pending Transactions
												</span>
												<span className="font-semibold">
													{dashboardData.earnings.pending_transactions}
												</span>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</motion.div>

						{/* Quick Stats */}
						<motion.div
							initial={{ opacity: 0, x: 20 }}
							animate={{ opacity: 1, x: 0 }}
							transition={{ duration: 0.6, delay: 0.5 }}
						>
							<Card className="glass-card border-0 shadow-lg">
								<CardHeader>
									<CardTitle className="text-lg font-karla">
										Quick Stats
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="space-y-4">
										<div className="flex items-center justify-between">
											<div className="flex items-center space-x-3">
												<div className="w-10 h-10 bg-newTheme-lime rounded-lg flex items-center justify-center">
													<Package className="h-5 w-5 text-newTheme-orange" />
												</div>
												<div>
													<h5 className="text-sm text-black font-karla">
														Service Packages
													</h5>
													<p className="text-lg font-semibold text-gray-900">
														{dashboardData.stats.packages_count}
													</p>
												</div>
											</div>
										</div>

										<div className="flex items-center justify-between">
											<div className="flex items-center space-x-3">
												<div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center">
													<Activity className="h-5 w-5 text-teal-600" />
												</div>
												<div>
													<h5 className="text-sm text-black font-karla">
														Total Appointments
													</h5>
													<p className="text-lg font-semibold text-gray-900">
														{dashboardData.stats.total_appointments}
													</p>
												</div>
											</div>
										</div>
									</div>
								</CardContent>
							</Card>
						</motion.div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default DoulaDashboard;
